import type { UiStreamAction } from "@intelligems/ig-types/schemas/ai/ui-stream-actions";
import isEqual from "lodash.isequal";
import { readToolJsonResults } from "@src/features/ai/utils/tool-json-results";
import type { AiChatState } from "@intelligems/ig-types/schemas/ai/ai-chat-state";

export interface CompletedAgenticLogicRule {
  id: string;
  managedBy: "builder" | "code";
}

export interface AgenticLogicCreationTracker {
  toolCallsById: Record<string, TrackedToolCall>;
  createdCodeRuleId?: string;
  completedRule?: CompletedAgenticLogicRule;
  rejectedRuleIds: Record<string, true>;
}

type TrackedToolCall =
  | { status: "consumed" }
  | {
      status: "pending";
      input: unknown;
      managedBy?: CompletedAgenticLogicRule["managedBy"];
      ruleId?: string;
      toolName: string;
    };

export const createAgenticLogicCreationTracker =
  (): AgenticLogicCreationTracker => ({
    rejectedRuleIds: {},
    toolCallsById: {},
  });

export function trackAgenticLogicCreation(
  tracker: AgenticLogicCreationTracker,
  action: UiStreamAction
): AgenticLogicCreationTracker {
  if (tracker.completedRule) {
    return tracker;
  }
  if (action.type !== "addStreamPart") {
    return tracker;
  }
  const part = action.streamPart;

  if (part.type === "tool-input-available") {
    return trackToolInput(tracker, part);
  }
  if (part.type === "tool-output-available") {
    return trackToolOutput(tracker, part);
  }
  return tracker;
}

/**
 * The creation a saved transcript records, read the way the live stream is.
 *
 * A conversation restored from history streams nothing, so a rule its turn
 * created while no page was attached (a reload mid-create) never reaches the
 * live tracker. Its tool calls are in the transcript as `dynamic-tool` parts —
 * input and, once finished, output — and are fed through the same steps.
 */
export function trackAgenticLogicCreationInTranscript(
  turns: AiChatState["turns"]
): AgenticLogicCreationTracker {
  let tracker = createAgenticLogicCreationTracker();
  for (const turn of turns) {
    for (const node of turn.nodes) {
      for (const message of node.messages ?? []) {
        for (const part of message.parts ?? []) {
          if (tracker.completedRule) {
            return tracker;
          }
          if (part.type !== "dynamic-tool") {
            continue;
          }
          tracker = trackToolInput(tracker, {
            toolCallId: part.toolCallId,
            toolName: part.toolName,
            input: readTranscriptInput(part.input),
          });
          if (part.state === "output-available") {
            tracker = trackToolOutput(tracker, {
              toolCallId: part.toolCallId,
              output: part.output,
            });
          }
        }
      }
    }
  }
  return tracker;
}

// A Claude-routed node stores tool input as the JSON text it streamed.
function readTranscriptInput(input: unknown): unknown {
  if (typeof input !== "string") {
    return input;
  }
  try {
    return JSON.parse(input);
  } catch {
    return input;
  }
}

function trackToolInput(
  tracker: AgenticLogicCreationTracker,
  part: { toolCallId: string; toolName: string; input: unknown }
): AgenticLogicCreationTracker {
  const existingCall = tracker.toolCallsById[part.toolCallId];
  if (existingCall) {
    if (existingCall.status === "consumed") {
      return tracker;
    }
    if (
      existingCall.toolName === part.toolName &&
      isEqual(existingCall.input, part.input)
    ) {
      return tracker;
    }
    return consumeToolCall(tracker, part.toolCallId);
  }

  const ruleId = readRuleId(part.input);
  const managedBy = readManagedBy(part.input);
  return {
    ...tracker,
    toolCallsById: {
      ...tracker.toolCallsById,
      [part.toolCallId]: {
        status: "pending",
        input: part.input,
        toolName: part.toolName,
        ...(managedBy ? { managedBy } : {}),
        ...(ruleId ? { ruleId } : {}),
      },
    },
  };
}

function trackToolOutput(
  tracker: AgenticLogicCreationTracker,
  part: { toolCallId: string; output: unknown }
): AgenticLogicCreationTracker {
  const toolCall = tracker.toolCallsById[part.toolCallId];
  if (toolCall?.status === "consumed") {
    return tracker;
  }
  const consumedTracker = consumeToolCall(tracker, part.toolCallId);
  if (!toolCall) {
    return consumedTracker;
  }

  const results = readToolJsonResults(part.output);
  if (!results) {
    return consumedTracker;
  }

  if (toolCall.toolName === "create_agentic_logic_rule") {
    const rule = readCreatedRule(results);
    if (!rule) {
      return consumedTracker;
    }
    if (toolCall.managedBy !== rule.managedBy) {
      return {
        ...consumedTracker,
        rejectedRuleIds: {
          ...consumedTracker.rejectedRuleIds,
          [rule.id]: true,
        },
      };
    }
    if (rule.managedBy === "builder") {
      return { ...consumedTracker, completedRule: rule };
    }
    return { ...consumedTracker, createdCodeRuleId: rule.id };
  }

  if (toolCall.toolName === "save_agentic_logic_rule_draft") {
    const savedRuleId = readSavedRuleId(results);
    if (
      !toolCall.ruleId ||
      !savedRuleId ||
      toolCall.ruleId !== savedRuleId ||
      tracker.rejectedRuleIds[savedRuleId] ||
      (tracker.createdCodeRuleId && tracker.createdCodeRuleId !== savedRuleId)
    ) {
      return consumedTracker;
    }
    return {
      ...consumedTracker,
      completedRule: {
        id: savedRuleId,
        managedBy: "code",
      },
    };
  }

  return consumedTracker;
}

function consumeToolCall(
  tracker: AgenticLogicCreationTracker,
  toolCallId: string
): AgenticLogicCreationTracker {
  return {
    ...tracker,
    toolCallsById: {
      ...tracker.toolCallsById,
      [toolCallId]: { status: "consumed" },
    },
  };
}

function readManagedBy(
  value: unknown
): CompletedAgenticLogicRule["managedBy"] | null {
  if (!value || typeof value !== "object" || !("managedBy" in value)) {
    return null;
  }
  return value.managedBy === "builder" || value.managedBy === "code"
    ? value.managedBy
    : null;
}

function readRuleId(value: unknown): string | null {
  if (!value || typeof value !== "object" || !("ruleId" in value)) {
    return null;
  }
  return readNonEmptyString(value.ruleId);
}

function readNonEmptyString(value: unknown): string | null {
  return typeof value === "string" && value.trim().length > 0 ? value : null;
}

function readSavedRuleId(results: Record<string, unknown>): string | null {
  if (!("draft" in results)) {
    return null;
  }
  return readRuleId(results.draft);
}

function readCreatedRule(
  results: Record<string, unknown>
): CompletedAgenticLogicRule | null {
  const rule = results.rule;
  if (!rule || typeof rule !== "object") {
    return null;
  }
  const fields = rule as Record<string, unknown>;
  const id = readNonEmptyString(fields.id);
  const { managedBy } = fields;
  if (!id || (managedBy !== "builder" && managedBy !== "code")) {
    return null;
  }
  return { id, managedBy };
}
